/**
 * Page 5 du wizard - Section Métier
 * Permet de sélectionner un métier et de remplir un formulaire dynamique
 */

import { useState, useEffect } from 'react';
import { Briefcase } from 'lucide-react';
import {
  METIERS,
  getMetierFormConfig,
  createEmptyMetierBesoin,
  type MetierBesoin,
  type MetierField,
  METIER_BESOIN_VERSION,
} from '../domain/metier/metierTypes';

interface MetierPageProps {
  initialMetierBesoin?: MetierBesoin | null;
  onMetierBesoinChange: (metierBesoin: MetierBesoin | null) => void;
}

export default function MetierPage({ initialMetierBesoin, onMetierBesoinChange }: MetierPageProps) {
  const [selectedMetierId, setSelectedMetierId] = useState<string>(initialMetierBesoin?.metierId || '');
  const [metierBesoin, setMetierBesoin] = useState<MetierBesoin | null>(
    initialMetierBesoin || null
  );

  const metierFormConfig = selectedMetierId ? getMetierFormConfig(selectedMetierId) : undefined;

  // Initialiser metierBesoin quand un métier est sélectionné
  useEffect(() => {
    if (selectedMetierId && !metierBesoin) {
      const empty = createEmptyMetierBesoin(selectedMetierId);
      setMetierBesoin(empty);
      onMetierBesoinChange(empty);
    } else if (selectedMetierId && metierBesoin && metierBesoin.metierId !== selectedMetierId) {
      // Si le métier change, réinitialiser
      const empty = createEmptyMetierBesoin(selectedMetierId);
      setMetierBesoin(empty);
      onMetierBesoinChange(empty);
    } else if (!selectedMetierId) {
      setMetierBesoin(null);
      onMetierBesoinChange(null);
    }
  }, [selectedMetierId]);

  // Charger les données initiales
  useEffect(() => {
    if (initialMetierBesoin) {
      setSelectedMetierId(initialMetierBesoin.metierId);
      setMetierBesoin(initialMetierBesoin);
    }
  }, [initialMetierBesoin]);

  const handleFieldChange = (fieldId: string, value: string | string[]) => {
    if (!metierBesoin) return;

    const updated = {
      ...metierBesoin,
      [fieldId]: value,
      version: METIER_BESOIN_VERSION, // Toujours mettre à jour la version
    };

    setMetierBesoin(updated);
    onMetierBesoinChange(updated);
  };

  const renderField = (field: MetierField) => {
    if (!metierBesoin) return null;

    const value = metierBesoin[field.id];

    if (field.type === 'textarea') {
      return (
        <textarea
          value={(value as string) || ''}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          placeholder={field.placeholder}
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 resize-none focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
        />
      );
    }

    if (field.type === 'select') {
      return (
        <select
          value={(value as string) || ''}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
        >
          <option value="">Sélectionnez...</option>
          {field.options?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'multiline_list') {
      // Pour multiline_list, on stocke un string[] mais on affiche un textarea
      const textValue = Array.isArray(value) ? value.join('\n') : '';
      return (
        <div className="space-y-2">
          <textarea
            value={textValue}
            onChange={(e) => {
              // Split par \n, trim chaque ligne, filtrer les lignes vides
              const lines = e.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => line.length > 0);
              handleFieldChange(field.id, lines);
            }}
            placeholder={field.placeholder}
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 resize-none focus:ring-2 focus:ring-bz-blue focus:border-bz-blue font-mono"
          />
          <p className="text-[10px] text-gray-500">
            Une valeur par ligne. Les lignes vides seront ignorées.
          </p>
        </div>
      );
    }

    if (field.type === 'multiselect') {
      const selectedValues = Array.isArray(value) ? value : [];
      const maxSelections = field.maxSelections;
      return (
        <div className="space-y-2">
          {field.options?.map((opt) => {
            const isSelected = selectedValues.includes(opt);
            const isDisabled = maxSelections ? selectedValues.length >= maxSelections && !isSelected : false;
            return (
              <label
                key={opt}
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-bz-blue bg-bz-blue/5'
                    : isDisabled
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                      : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={(e) => {
                    const current = selectedValues;
                    if (e.target.checked) {
                      if (!maxSelections || current.length < maxSelections) {
                        handleFieldChange(field.id, [...current, opt]);
                      }
                    } else {
                      handleFieldChange(field.id, current.filter((v) => v !== opt));
                    }
                  }}
                  className="mt-1"
                />
                <span className="text-xs text-gray-700">{opt}</span>
              </label>
            );
          })}
          {maxSelections && (
            <p className="text-[10px] text-gray-500 italic">
              Sélectionnez jusqu&apos;à {maxSelections} options ({selectedValues.length}/{maxSelections} sélectionnées)
            </p>
          )}
        </div>
      );
    }

    // text
    return (
      <input
        type="text"
        value={(value as string) || ''}
        onChange={(e) => handleFieldChange(field.id, e.target.value)}
        placeholder={field.placeholder}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
      />
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
            <Briefcase className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-xs font-semibold text-purple-600 uppercase">Phase 5 sur 5</p>
            <h3 className="text-base font-semibold text-gray-900">Prise de besoin – Métier du client</h3>
          </div>
        </div>

        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-900">
            Cette section permet de comprendre les réalités spécifiques du métier du client afin de mieux aligner les solutions TI.
          </p>
        </div>

        {/* Sélection du métier */}
        <div className="mb-6">
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Sélectionnez le métier du client <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedMetierId}
            onChange={(e) => setSelectedMetierId(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
          >
            <option value="">-- Choisir un métier --</option>
            {METIERS.map((metier) => (
              <option key={metier.id} value={metier.id}>
                {metier.label}
              </option>
            ))}
          </select>
        </div>

        {/* Formulaire métier dynamique */}
        {!selectedMetierId ? (
          <div className="p-6 text-center bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-600">
              Veuillez sélectionner un métier pour continuer la prise de besoin métier.
            </p>
          </div>
        ) : metierFormConfig ? (
          <div className="space-y-5">
            <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="text-sm font-semibold text-purple-900 mb-1">{metierFormConfig.titre}</h4>
              <p className="text-xs text-purple-700">{metierFormConfig.description}</p>
            </div>

            {metierFormConfig.fields.map((field) => (
              <div key={field.id} className="space-y-2">
                <label className="block text-xs font-semibold text-gray-700">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderField(field)}
                {field.helpText && (
                  <p className="text-[10px] text-gray-500 italic">{field.helpText}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-yellow-800">
              Configuration non trouvée pour le métier sélectionné.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

